import React from 'react';
import { InputLabel, Select, TextField } from "@mui/material";
import { styled } from '@mui/material/styles';
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import FormControl from '@mui/material/FormControl';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 120,
    width: '100%',
    maxWidth: '100%',
    padding: '2px 4px',
    color: '#CCCCCC',
    '& .note': {
        color: '#A9A9A9',
        fontSize: '10px',
        textAlign: 'left'
    },
    '& .chevronIcon': {
        marginLeft: '10px',
        marginTop: '5px',
        color: '#357ebd',
        marginRight: '7px'
    }
}));

const StyledCloseBtn = styled('div')({
    width: '100%',
    position: 'relative',
    height: 20,
    display: 'flex',
    alignItems: 'center',
    padding: '0px 5px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    '&:focus': {
        outline: 'none'
    }
});

const StyledSearchBarFixed = styled('div')({
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 10,
    padding: '8px 16px',
});

const MenuPaperProps = {
    maxHeight: 500,
};

class ReactMultipleSelect extends React.Component {
    constructor(props) {
        super(props);
        const { defaultCheckedAll, optionsList } = props;
        this.searchInputRef = React.createRef();
        this.state = {
            open: false,
            searchValue: '',
            value: defaultCheckedAll ? optionsList : [],
            firstRender: true,
            selectAllChecked: defaultCheckedAll === undefined ? false : true,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.firstRender && prevProps.value.length > 0) {
            this.setState({ firstRender: false });
        }
    }

    handleOpen = (isOpen) => {
        if (!isOpen) {
            this.setState({ searchValue: "" });
        }
        this.setState({
            open: isOpen,
        });
    };

    handleSearchChange = (event) => {
        const searchValue = event.target.value;
        this.setState({ searchValue }, () => {
            if (this.searchInputRef.current) {
                this.searchInputRef.current.focus();
            }
            const { value } = this.state;
            const { optionsList } = this.props;
    
            const filteredOptionsList = optionsList.filter(option =>
                option.label.toLowerCase().includes(searchValue.toLowerCase())
            );

            const allFilteredSelected = filteredOptionsList.every(option =>
                value.some(selected => selected && selected.value === option.value)
            );

            this.setState({ selectAllChecked: allFilteredSelected });
        });
    };

    handleKeyDown = (event) => {
        event.stopPropagation();
    };

    handleSelectChange = (event) => {
        const isCloseMenu = event.nativeEvent?.target?.id === 'closeMenu' || event.nativeEvent?.target?.closest?.('#closeMenu');
        if (isCloseMenu ||
            (event.nativeEvent?.target?.tagName === 'INPUT' && event.nativeEvent?.target?.type === 'text')
        ) {
            return;
        }

        let newValue = event.target.value;
        if (Array.isArray(newValue)) {
            newValue = newValue.filter(v => v !== undefined && v !== null);
        }

        const { onChange, target, optionsList } = this.props;

        this.setState({ value: newValue });
        onChange(target, newValue);

        if(newValue.length == optionsList.length) {
            this.setState({selectAllChecked: true})
        } else {
            this.setState({selectAllChecked: false})
        }
    };

    handleSelectAll = (event) => {
        event.stopPropagation();
        const { onChange, target, optionsList } = this.props;
        const { selectAllChecked, searchValue } = this.state;
    
        const filteredOptionsList = optionsList.filter(option =>
            option.label.toLowerCase().includes(searchValue.toLowerCase())
        );
    
        if (selectAllChecked) {
            this.setState({ value: [], selectAllChecked: false });
            onChange(target, []);
        } else {
            this.setState({ value: filteredOptionsList, selectAllChecked: true });
            onChange(target, filteredOptionsList);
        }
    };

    render() {
        const renderValues = (multiple, selected) => {
            let renderString = '';
            const filteredSelected = selected.filter(item => item && item.label);
            if (multiple) {
                renderString = filteredSelected.map(item => item.label).join(', ');
            } else {
                renderString = filteredSelected.length === 1 ? `${filteredSelected[0].label}` : `${filteredSelected.length} selected`;            
            }
            return renderString;
        };

        const { optionsList, valueStateName, onChange, labelText, target, note, renderMultipleValues = true, enabledSearch = false, defaultCheckedAll = false, ...other } = this.props;
        const { open, searchValue, value, selectAllChecked, firstRender } = this.state;

        const filteredOptionsList = optionsList.filter(option =>
            option.label.toLowerCase().includes(searchValue.toLowerCase())
        );

        const MenuProps = {
            disableAutoFocusItem: true,
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "left"
            },
            PaperProps: {
                style: MenuPaperProps
            }
        };

        return (
            <StyledFormControl variant="outlined">
                <InputLabel id="demo-simple-select-outlined-label">{defaultCheckedAll && value.length > 0 ? "" : labelText}</InputLabel>
                <Select
                    IconComponent={(props) => (
                        <div {...props}>
                            <div className={"pull-right"}>
                                <FontAwesomeIcon className="chevronIcon" icon={faChevronDown} />
                            </div>
                        </div>
                    )}
                    displayEmpty={defaultCheckedAll}
                    value={value}
                    name={valueStateName}
                    onChange={this.handleSelectChange}
                    label={defaultCheckedAll && value.length > 0 ? undefined : labelText}
                    multiple
                    renderValue={(selected) => {
                        if (defaultCheckedAll && firstRender) {
                            return optionsList.map(item => item.label).join(', ');
                        } else {
                            return renderValues(renderMultipleValues, selected);
                        }
                    }}
                    MenuProps={MenuProps}
                    open={open}
                    onClose={() => this.handleOpen(false)}
                    onOpen={() => this.handleOpen(true)}
                    {...other}
                >
                    <StyledCloseBtn id='closeMenu' onMouseDown={(event) => { event.stopPropagation(); this.handleOpen(false); }} onClick={(event) => { event.stopPropagation(); this.handleOpen(false); }}>Close</StyledCloseBtn>
                    {enabledSearch && (
                        <StyledSearchBarFixed onClick={(event) => event.stopPropagation()}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Search..."
                                value={searchValue}
                                onChange={this.handleSearchChange}
                                onKeyDown={this.handleKeyDown}
                                inputRef={this.searchInputRef}
                            />
                        </StyledSearchBarFixed>
                    )}
                    <MenuItem
                        id="selectAllMenu"
                        onClick={this.handleSelectAll}
                        style={{
                            margin: 0,
                            padding: 0,
                            height: 50,
                        }}
                    >
                        <Checkbox
                            checked={selectAllChecked}
                            onClick={this.handleSelectAll}
                            style={{
                                padding: '0 0 0 24px',
                            }}
                        />
                        <ListItemText
                            primary="Select All"
                            onClick={this.handleSelectAll}
                            style={{
                                flex: 1,
                                padding: '12.5px 10px',
                                height: '100%'
                            }}
                        />
                    </MenuItem>
                    {filteredOptionsList?.map((option) => (
                        <MenuItem key={option.value} value={option}>
                            <Checkbox checked={value?.some(filter => filter?.value === option.value)} />
                            {option.multiSelectLogo} <ListItemText primary={option.label} />
                        </MenuItem>
                    ))}
                </Select>
                <span><small className="note">{note}</small></span>
            </StyledFormControl>
        );
    }
}

export default ReactMultipleSelect;
